import { React, useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { marked } from "marked";
import Quill from "quill";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "react-quill/dist/quill.bubble.css";
import { convertMarkdownToHtml, styles } from "../utils/convertMarkdownToHTML";
import DOMPurify from "dompurify";
import FeedbackPopup from "./FeedbackPopup";

const fonts = [
  "Arial", "Comic Sans MS", "Courier New", "Georgia", "Helvetica",
  "Lucida Sans", "Times New Roman", "Verdana", "Tahoma",
  "Trebuchet MS", "Impact", "Monospace"
];

const fontClassNames = fonts.map(font => font.toLowerCase().replace(/\s+/g, "-"));

const CustomToolbar = () => (
  <div id="toolbar-container" className="p-2">
    {/* Toolbar content omitted for brevity */}
  </div>
);

export default function ChatMessage({
  message,
  parentRefreshClick,
  isLoading,
  displayMode,
  wordCount,
  charCount,
  handleDisplayModeChange,
  showCount,
  from,
  user_id,
  client_organization_id,
  grant_proposal_form_id,
  onRefreshPdf,
  docxData,
  setDocxData,
  convertToDocx,
  convertToPdf,
  scrollIntoView = false // NEW PROP
}) {
  const messageRef = useRef(null);
  const [messageRefreshed, setMessageRefreshed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tabList, setTabList] = useState([]);
  const [messageText, setMessageText] = useState(message.message);
  const [currentWordCount, setCurrentWordCount] = useState(0);
  const [currentCharCount, setCurrentCharCount] = useState(0);
  const [userMessageText, setUserMessageText] = useState(message.message);
  const [isUserEditing, setIsUserEditing] = useState(false);

  const baseurl = process.env.REACT_APP_API_URL;

  const convertMarkdownToHtml = (markdown) => {
    return DOMPurify.sanitize(marked(markdown));
  };

  useEffect(() => {
    const Font = ReactQuill.Quill.import("formats/font");
    Font.whitelist = fontClassNames;
    ReactQuill.Quill.register(Font, true);
  }, []);

  const modules = {
    toolbar: "#toolbar-container",
  };

  const calculateWNS = (message) => {
    const words = message.trim().split(/\s+/);
    const characters = message.trim().length;
    setCurrentWordCount(words.length);
    setCurrentCharCount(characters);
  };

  const handleContentInput = (e) => {
    calculateWNS(e.target.innerText);
  };

  const updateSelected = (selectedIdx) => {
    setTabList((prev) => {
      let newList = [...prev];
      let newObj = { selected: false };
      newList = newList.slice(selectedIdx, 1, newObj);
      return newList;
    });
  };

  const refreshClick = (e) => {
    parentRefreshClick();
    setMessageRefreshed(true);
    setTabList((prev) => {
      if (prev) {
        prev.map((elementInPrev) => {
          if (elementInPrev) {
            elementInPrev.selected = false;
          }
        });
        return [...prev, { selected: true }];
      }
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    if (message.user === "user") {
      const updatedDocxData = docxData.map((msg) =>
        msg.id === message.id ? { ...msg, message: userMessageText } : msg
      );
      setDocxData(updatedDocxData);
      onRefreshPdf(updatedDocxData);
      setIsUserEditing(false);
    } else {
      const updatedDocxData = docxData.map((msg) =>
        msg.timestamp === message.timestamp
          ? { ...msg, message: messageText }
          : msg
      );
      setDocxData(updatedDocxData);
      onRefreshPdf(updatedDocxData);
      setIsEditing(false);
    }

    if (message.user === "bot") {
      try {
        const response = await fetch(`${baseurl}/update-chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id,
            client_id: client_organization_id,
            proposal_id: grant_proposal_form_id,
            message: messageText,
            question: message.question,
            timestamp: message.timestamp || new Date().toISOString(),
          }),
        });

        const data = await response.json();
        if (data.success) {
          setIsEditing(false);
        }
      } catch (error) {
        console.error("Error updating message:", error);
      }
    }
  };

  const handleCancelClick = () => {
    setMessageText(message.message);
    setIsEditing(false);
  };

  // SCROLL TO LAST MESSAGE
  useEffect(() => {
    if (scrollIntoView && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollIntoView]);

  useEffect(() => {
    calculateWNS(message.message);
  }, [message.message]);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  useEffect(() => {
    const quillOverride = document.createElement("style");
    quillOverride.innerHTML = `
      /* kill Quill’s built-in scrollbars inside each message */
      .chat-message-center .ql-container,
      .chat-message-center .ql-editor {
        max-height: none !important;
        height: auto      !important;
        overflow: visible !important;
      }
    `;
    document.head.appendChild(quillOverride);
    return () => {
      document.head.removeChild(quillOverride);
    };
  }, []);


  const handleTextChange = (text) => {
    setMessageText(text);
  };

  return (
    <div
      ref={messageRef}
      className={`chat-message ${message.user === "bot" && "chatgpt w-full overscroll-contain scroll-smooth custom-scrollbar"} w-full`}
    >
      <div className="chat-message-center text-left overscroll-contain w-full scroll-smooth custom-scrollbar">
        <div className="avatar-and-btns-div">
          {from !== "copilot" && (
            <>
              <div className={`${message.user === "bot" && "chatgpt editor-container"}`}>
                {message.user !== "bot" && (
                  <>
                    {!isUserEditing ? (
                      <div className="text-sm whitespace-pre-wrap break-words">{userMessageText}</div>
                    ) : (
                      <textarea
                        value={userMessageText}
                        onChange={(e) => {
                          setUserMessageText(e.target.value);
                          e.target.style.height = "auto";
                          e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                        className="bg-[#9D4EDD] rounded-md border-2 w-full p-2 resize-none overflow-y-auto"
                        cols={200}
                        rows={12}
                      />
                    )}
                    <div className="message-controls flex items-center justify-start space-x-2">
                      {!isUserEditing ? (
                        <button
                          className="edit-button text-green-500 underline px-1 py-1"
                          onClick={() => setIsUserEditing(true)}
                        >
                          Edit
                        </button>
                      ) : (
                        <div className="edit-controls flex gap-2 mt-2">
                          <button
                            className="save-button text-green-500 underline px-4"
                            onClick={handleSaveClick}
                          >
                            Save
                          </button>
                          <button
                            className="cancel-button text-red-500 underline px-4"
                            onClick={() => {
                              setUserMessageText(message.message);
                              setIsUserEditing(false);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {message.user === "bot" && showCount && (
                <div className="word-char-count-container">
                  <div className="flex flex-col gap-1 word-char-count-btn">
                    <button
                      className={`word-count w-8 h-8 rounded text-white transition-all !bg-[#9D4EDD] ${
                        (displayMode ?? "words") === "words"
                          ? "brightness-125"
                          : "brightness-75"
                      }`}
                      onClick={() => handleDisplayModeChange("words")}
                    >
                      w
                    </button>
                    <button
                      className={`character-count w-8 h-8 rounded text-white transition-all !bg-[#9D4EDD] ${
                        (displayMode ?? "words") === "characters"
                          ? "brightness-125"
                          : "brightness-75"
                      }`}
                      onClick={() => handleDisplayModeChange("characters")}
                    >
                      c
                    </button>
                  </div>
                  <div className="mt-1">
                    <span className="total-number-field">
                      {displayMode === "words" ? currentWordCount : currentCharCount}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {message.user === "bot" && messageRefreshed && (
          <div className="tab-bar-div w-full">
            {tabList.map((tab, idx) =>
              tab ? (
                <div
                  onClick={() => updateSelected(idx)}
                  key={idx}
                  className={classNames("tab", tab.selected && "selected")}
                />
              ) : null
            )}
          </div>
        )}

        <div className="editor-container">
          {message.user === "bot" && (
            <>
              <div className="quilljs-container">
                {isEditing && <CustomToolbar />}
                <ReactQuill
                  key={isEditing ? "editing" : "reading"}
                  value={convertMarkdownToHtml(messageText)}
                  onChange={handleTextChange}
                  modules={isEditing ? modules : { toolbar: false }}
                  readOnly={!isEditing}
                  theme={isEditing ? "snow" : "bubble"}
                />
              </div>
              <div className="message-controls flex items-center justify-start space-x-2">
                {!isEditing ? (
                  <>
                    <button
                      className="edit-button text-green-500 underline px-4 py-1"
                      onClick={handleEditClick}
                    >
                      Edit
                    </button>
                    <FeedbackPopup />
                  </>
                ) : (
                  <div className="edit-controls flex gap-2 mt-2">
                    <button
                      className="save-button text-green-500 underline px-4"
                      onClick={handleSaveClick}
                    >
                      Save
                    </button>
                    <button
                      className="cancel-button text-red-500 underline px-4"
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
